// Value imports
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUIStateStore } from "@/store/uiStateStore";

import { useGetMyWriters } from "../../../../../../api/queries";
import { Button } from "../../../../../../newComponents/button/Button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../../../../../../newComponents/dialog/Dialog";
import { IconBin, IconCreate, IconUser, IconUserEdit } from "../../../../../../newComponents/icon/Icon";
import Text from "../../../../../../newComponents/typography/Text";
import useAppStore from "../../../../../../store/appStateZustand";
import CreateWriterModal from "../components/CreateWriterModal";
import SelectExistingWritersModal from "../components/SelectExistingWritersModal";
import useWriterStore from "../store/writerStore";

export default function ChooseWritersStep() {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [editingWriter, setEditingWriter] = useState<string | null>(null);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const {
    selectedWriters,
    removeSelectedWriter,
    setAvailableWriters,
  } = useWriterStore();

  // Fetch available writers from API
  const writersQuery = useGetMyWriters({ aac: selectedAccount as string });

  // Update available writers in store when query succeeds
  useEffect(() => {
    if (writersQuery.data?.writers) {
      setAvailableWriters(writersQuery.data.writers);
    }
  }, [writersQuery.data, setAvailableWriters]);

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg text-old-sentric-dark-gray"}`}>
      {/* Header with buttons */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-center justify-between w-full gap-3 md:flex-row">
          <Dialog open={isSelectModalOpen} onOpenChange={setIsSelectModalOpen}>
            <DialogTrigger className="w-full md:w-1/2" asChild>
              <Button
                icon={<IconUser className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                variant="primary"
                label={t("album:add-existing-writers") ?? "Add Existing Writers"}
              />
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <SelectExistingWritersModal onClose={() => setIsSelectModalOpen(false)} />
            </DialogContent>
          </Dialog>

          <Dialog
            open={isCreateModalOpen}
            onOpenChange={(open) => {
              setIsCreateModalOpen(open);
              if (!open)
                setEditingWriter(null);
            }}
          >
            <DialogTrigger className="w-full md:w-1/2" asChild>
              <Button
                icon={<IconCreate className={`${isTunecore ? "stroke-white" : "stroke-black"}`} />}
                variant="primary"
                label={t("album:create-new-writer") ?? "Create New Writer"}
              />
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <CreateWriterModal
                onClose={() => {
                  setIsCreateModalOpen(false);
                  setEditingWriter(null);
                }}
                editingWriterCode={editingWriter}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Selected writers list */}
      <div className="space-y-4">

        {/* Writers list */}
        {selectedWriters.length === 0 ? (
          <div className="py-8 text-center border rounded-lg border-sentric-mid-grey">
            <Text as="p">
              {t("album:no-writers-selected-use-buttons") ?? "No writers selected. Use the buttons above to add writers for this album."}
            </Text>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedWriters.map(writer => (
              <div
                key={writer.writercode}
                className={`p-4 transition-colors rounded-lg ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border  border-sentric-mid-grey "}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <Text as="p" className="font-semibold">
                      {writer.writername}
                    </Text>
                    <Text as="p" className="mt-1 text-sm">
                      {t("album:cae-ipi-label")}
                      :
                      {" "}
                      {writer.caeipinumber ?? t("album:n-a")}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      tooltip
                      tooltipText={t("album:edit-writer") ?? "Edit Writer"}
                      variant="link"
                      onClick={() => {
                        setEditingWriter(writer.writercode);
                        setIsCreateModalOpen(true);
                      }}
                      icon={<IconUserEdit className={`${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                      className="text-sentric-blue hover:text-sentric-blue/80 group hover:scale-110"
                      title={t("album:edit-writer") ?? "Edit writer"}
                    />
                    <Button
                      tooltip
                      tooltipText={t("album:remove-writer") ?? "Remove Writer"}
                      variant="link"
                      onClick={() => removeSelectedWriter(writer.writercode)}
                      icon={<IconBin className={`${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                      className="text-red-600 hover:text-red-800 group hover:scale-110"
                      title={t("album:remove-writer") ?? "Remove writer"}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
